import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";
import { errMsg, pretty } from "@/lib/residentApi";
import { SPECIAL_CARDS, usePlacedItems } from "@/lib/placement";
import { Switch } from "@/components/ui/switch";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/roles`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
const api = {
  get: () => axios.get(BASE, h()).then((r) => r.data),
  post: (p, b) => axios.post(`${BASE}${p}`, b, h()).then((r) => r.data),
  patch: (p, b) => axios.patch(`${BASE}${p}`, b, h()).then((r) => r.data),
  del: (p) => axios.delete(`${BASE}${p}`, h()).then((r) => r.data),
};
const box = "p-3 border border-white/10 rounded-sm space-y-2";
const head = "font-mono text-[9px] tracking-[0.18em] uppercase text-nex-muted";
const input = "px-2 py-1.5 bg-black/40 border border-white/15 rounded-sm text-sm text-nex-silver focus:outline-none focus:border-nex-gold/60";

function Perms({ role, catalog, save }) {
  const perms = catalog[role.audience];
  const toggle = (k, on) => save({ permissions: on ? [...role.permissions, k] : role.permissions.filter((x) => x !== k) });
  return (
    <div className={box}><div className={head}>What they can do</div>
      {Object.entries(perms).map(([k, label]) => (
        <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
          <Switch checked={role.permissions.includes(k)} onCheckedChange={(v) => toggle(k, v)} data-testid={`role-perm-${role.id}-${k}`} />
          <span><span className="text-nex-silver">{pretty(k)}</span><span className="block text-[11px] text-nex-muted">{label}</span></span>
        </label>))}
    </div>
  );
}

function Grants({ role, specials, save }) {
  const on = role.special_cards || [];
  const toggle = (id, v) => save({ special_cards: v ? [...on, id] : on.filter((x) => x !== id) });
  return (
    <div className={box} data-testid={`role-grants-${role.id}`}><div className={head}>Bank &amp; Debt access (off unless you turn it on)</div>
      {specials.map((c) => (
        <label key={c.id} className="flex items-start gap-3 text-sm cursor-pointer">
          <Switch checked={on.includes(c.id)} onCheckedChange={(v) => toggle(c.id, v)} data-testid={`role-grant-${role.id}-${c.id}`} />
          <span><span className="text-nex-silver">{c.label}</span><span className="block text-[11px] text-nex-muted">See and edit · {c.sections.join(" · ")}</span></span>
        </label>))}
    </div>
  );
}

function Cards({ role, save }) {
  const items = usePlacedItems().filter((c) => !SPECIAL_CARDS.includes(c.id));
  const groups = useMemo(() => items.reduce((a, c) => ({ ...a, [c.vertical_id || "other"]: [...(a[c.vertical_id || "other"] || []), c] }), {}), [items]);
  const all = role.cards == null;
  const sel = new Set(role.cards || items.map((c) => c.id));
  const toggle = (id, on) => save({ cards: on ? [...sel, id] : [...sel].filter((x) => x !== id) });
  return (
    <div className={box}><div className="flex items-center gap-2"><span className={head}>Desktop cards they see</span>
      <label className="ml-auto flex items-center gap-2 text-xs text-nex-muted">All cards <Switch checked={all} onCheckedChange={(v) => save(v ? { all_cards: true } : { cards: items.map((c) => c.id) })} data-testid={`role-cards-all-${role.id}`} /></label></div>
      {!all && Object.entries(groups).map(([g, cs]) => (
        <div key={g} className="space-y-1"><div className="text-[11px] text-nex-gold">{pretty(g)}</div>
          <div className="flex flex-wrap gap-1.5">{cs.map((c) => (
            <button key={c.id} type="button" onClick={() => toggle(c.id, !sel.has(c.id))} aria-pressed={sel.has(c.id)} data-testid={`role-card-${role.id}-${c.id}`}
              className={`px-2 py-1 border rounded-sm text-[11px] ${sel.has(c.id) ? "border-nex-gold/60 text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted"}`}>{c.label}</button>))}</div>
        </div>))}
    </div>
  );
}

function Members({ role, people, reload }) {
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const list = people[role.audience].filter((p) => !p.is_super_admin && `${p.name} ${p.email || ""}`.toLowerCase().includes(q.trim().toLowerCase()));
  const call = (on, ids, msg) => api.post(`/${role.id}/${on ? "assign" : "unassign"}`, { member_ids: ids }).then(() => { toast.success(msg); setSel([]); reload(); }).catch((e) => toast.error(errMsg(e)));
  const set = (p, on) => call(on, [p.id], on ? `${p.name} → ${role.title}` : `${p.name} removed`);
  const pick = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const out = list.filter((p) => p.role_id !== role.id);
  const inRole = list.filter((p) => p.role_id === role.id);
  return (
    <div className={box}><div className={head}>Who has this role</div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" data-testid={`role-member-search-${role.id}`} className={`${input} flex-1 min-w-[160px]`} />
        <button type="button" onClick={() => setSel(out.map((p) => p.id))} data-testid={`role-member-select-all-${role.id}`} className="text-[11px] text-nex-muted hover:text-nex-silver">Select all</button>
        <button type="button" disabled={!sel.length} onClick={() => call(true, sel, `${sel.length} assigned to ${role.title}`)} data-testid={`role-member-assign-selected-${role.id}`}
          className="px-3 py-1.5 border border-nex-gold/60 text-nex-gold rounded-sm text-xs disabled:opacity-40">Assign {sel.length || ""} selected</button>
        {inRole.length > 0 && <button type="button" onClick={() => window.confirm(`Remove ${inRole.length} ${inRole.length === 1 ? "person" : "people"} from ${role.title}?`) && call(false, inRole.map((p) => p.id), `${inRole.length} removed from ${role.title}`)} data-testid={`role-member-remove-all-${role.id}`} className="text-[11px] text-red-300 hover:text-red-200">Remove {q ? "shown" : "all"}</button>}
      </div>
      {inRole.map((p) => (
        <label key={p.id} className="flex items-center gap-3 text-sm cursor-pointer">
          <Switch checked onCheckedChange={() => set(p, false)} data-testid={`role-member-${role.id}-${p.id}`} />
          <span className="text-nex-silver">{p.name}</span><span className="text-[11px] text-nex-muted truncate">{p.email}</span>
        </label>))}
      {out.map((p) => (
        <label key={p.id} className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={sel.includes(p.id)} onChange={() => pick(p.id)} data-testid={`role-member-pick-${role.id}-${p.id}`} className="h-4 w-4 accent-[#d4a017]" />
          <span className="text-nex-muted">{p.name}</span><span className="text-[11px] text-nex-muted truncate">{p.email}{p.role_id ? " · has another role" : ""}</span>
        </label>))}
    </div>
  );
}

function RoleEditor({ role, d, reload }) {
  const save = (b) => api.patch(`/${role.id}`, b).then(reload).catch((e) => { toast.error(errMsg(e)); if (b.title) document.querySelector(`[data-testid="role-title-${role.id}"]`).value = role.title; });
  const remove = () => window.confirm(`Delete the role "${role.title}"? People in it go back to their own permissions.`) && api.del(`/${role.id}`).then(() => { toast.success("Role deleted"); reload(); });
  return (
    <div className="space-y-3" data-testid={`role-editor-${role.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input defaultValue={role.title} onBlur={(e) => e.target.value.trim() !== role.title && save({ title: e.target.value })} data-testid={`role-title-${role.id}`} className={`${input} flex-1 min-w-[180px]`} />
        <button type="button" onClick={remove} data-testid={`role-delete-${role.id}`} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><Trash2 className="h-3.5 w-3.5" /> Delete role</button>
      </div>
      <Perms role={role} catalog={d.catalog} save={save} />
      <Grants role={role} specials={d.special_cards} save={save} />
      <Cards role={role} save={save} />
      <Members role={role} people={d.people} reload={reload} />
    </div>
  );
}

/** Super Admin: create roles (title + who they're for), choose what each role can do and see, then assign people. */
export function RolesBoard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ title: "", audience: "staff" });
  const load = () => api.get().then(setD).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const create = (e) => {
    e.preventDefault();
    api.post("", { ...f, permissions: [] }).then((r) => { toast.success(`Role "${r.title}" created — now pick what it can do`); setF({ title: "", audience: f.audience }); setOpen(r.id); load(); }).catch((err) => toast.error(errMsg(err)));
  };
  return (
    <div className="space-y-4" data-testid="roles-board">
      <form onSubmit={create} className="flex flex-wrap items-center gap-2" data-testid="role-create-form">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="New role title (e.g. Leadership, House Lead, Mentor)" data-testid="role-create-title" className={`${input} flex-1 min-w-[220px]`} />
        {[["staff", "For staff"], ["partner", "For partners"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, audience: k })} aria-pressed={f.audience === k} data-testid={`role-create-audience-${k}`}
            className={`px-2.5 py-1.5 border rounded-sm font-mono text-[10px] uppercase tracking-[0.12em] ${f.audience === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/15 text-nex-muted"}`}>{l}</button>))}
        <button type="submit" disabled={!f.title.trim()} data-testid="role-create-submit" className="inline-flex items-center gap-1 px-3 py-1.5 border border-nex-gold/60 text-nex-gold rounded-sm text-xs disabled:opacity-40"><Plus className="h-3.5 w-3.5" /> Create role</button>
      </form>
      {!d.roles.length && <p className="text-sm text-nex-muted" data-testid="roles-empty">No roles yet.</p>}
      <ul className="space-y-2">{d.roles.map((r) => {
        const n = d.people[r.audience].filter((p) => p.role_id === r.id).length;
        return (
          <li key={r.id} className="border border-white/10 rounded-sm" data-testid={`role-${r.id}`}>
            <button type="button" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`role-open-${r.id}`} className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-nex-gold/5 transition-colors">
              <span className="flex-1 text-nex-silver font-medium">{r.title}</span>
              <span className="font-mono text-[10px] text-nex-muted uppercase">{r.audience}</span>
              <span className="text-[11px] text-nex-muted">{r.permissions.length} permission(s){` · ${r.cards == null ? "all cards" : `${r.cards.length} cards`}`}</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-nex-gold"><Users className="h-3 w-3" />{n}</span>
            </button>
            {open === r.id && <div className="px-3 pb-3"><RoleEditor role={r} d={d} reload={load} /></div>}
          </li>);
      })}</ul>
    </div>
  );
}
